import { Box, Divider, Stack } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactComponent as UserIcon } from 'assets/icons/UserIcon.svg'
import { getFutureDates } from 'components/shared/processes'
import { DeleteShiftDialog } from 'components/shared/shift-schedule/DeleteShiftDialog'
import { ShiftDayDetailsDrawer } from 'components/shared/shift-schedule/ShiftDayDetailsDrawer'
import { ShiftEmployeeDrawer } from 'components/shared/shift-schedule/ShiftEmployeeDrawer'
import moment from 'moment'
import {
	ShiftEmployee,
	ShiftEmployeeList,
} from 'components/shared/shift-schedule/ShiftEmployeeList'
import {
	ShiftCellAssignment,
	ShiftScheduleGrid,
} from 'components/shared/shift-schedule/ShiftScheduleGrid'
import { ShiftScheduleHeader } from 'components/shared/shift-schedule/ShiftScheduleHeader'
import { ShiftScheduleHorizontalScrollbar } from 'components/shared/shift-schedule/ShiftScheduleHorizontalScrollbar'
import {
	getDatesInRange,
	getEmployeeRowHeight,
	hideScrollbarSx,
	isPastScheduleDate,
	SHIFT_EMPLOYEE_COLUMN_WIDTH,
	SHIFT_SCHEDULE_STICKY_TOP_Z_INDEX,
	SHIFT_SCHEDULE_VERTICAL_SCROLL_MAX_HEIGHT,
	sortCellAssignments,
} from 'components/shared/shift-schedule/shiftScheduleLayout'
import { useShiftScheduleScrollLayout } from 'components/shared/shift-schedule/useShiftScheduleScrollLayout'
import { useGetDailyShifts } from 'hook/query/useGetDailyShifts'
import { useGetMilkersInfo } from 'hook/query/useGetMilkersInfo'
import { useGetMilkingParlorNames } from 'hook/query/useGetMilkingParlorNames'
import { ShiftOptions } from 'models/ICattlesData'
import { IMilkingScheduleAssignment } from 'models/MilkingProcess'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import MilkingProcessService from 'services/milkingProcessService'
import { COLORS } from 'styles/colors'
import { useAppDispatch } from 'store/hooks'
import { notifyUser } from 'store/notificationsSlice'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { MilkingShiftsHeader } from './components/header/MilkingShiftsHeader'

export const MilkingShiftsPage: FC = () => {
	const queryClient = useQueryClient()
	const dispatch = useAppDispatch()
	const [dateRange, setDateRange] = useState(getFutureDates(6))
	const [selectedParlors, setSelectedParlors] = useState<number[] | 'all'>(
		'all',
	)
	const [selectedShifts, setSelectedShifts] = useState<ShiftOptions[] | 'all'>(
		'all',
	)
	const [selectedCellKey, setSelectedCellKey] = useState<string | null>(null)
	const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
		null,
	)
	const [detailsDrawerData, setDetailsDrawerData] = useState<{
		employeeName: string
		assignments: ShiftCellAssignment[]
	} | null>(null)
	const [shiftToDelete, setShiftToDelete] = useState<{
		id: number
		employeeName: string
		title: string
		timeRange: string
		parlorName: string
	} | null>(null)
	const { data: milkersData = [] } = useGetMilkersInfo()
	const { data: parlorNames } = useGetMilkingParlorNames()
	const { data: shiftOptions = [] } = useGetDailyShifts({
		includeUnavailable: true,
	})
	const { data: schedules = [] } = useQuery<IMilkingScheduleAssignment[]>({
		queryKey: [
			'milking-schedules-grid',
			dateRange.startDate,
			dateRange.endDate,
		],
		queryFn: async () => {
			const result = await MilkingProcessService.getMilkingSchedules({
				start: dateRange.startDate,
				end: dateRange.endDate,
			})
			return result.items
		},
	})

	const employees = useMemo<ShiftEmployee[]>(
		() =>
			milkersData.map(milker => {
				const [lastname = '', ...rest] = milker.full_name.split(' ')
				return {
					id: milker.id,
					firstname: rest.join(' '),
					lastname,
					avatar: milker.photo,
				}
			}),
		[milkersData],
	)
	const filteredSchedules = useMemo(() => {
		return schedules.filter(item => {
			const isParlorMatched =
				selectedParlors === 'all' ? true : selectedParlors.includes(item.parlor)
			const isShiftMatched =
				selectedShifts === 'all' ? true : selectedShifts.includes(item.period)
			return isParlorMatched && isShiftMatched
		})
	}, [schedules, selectedParlors, selectedShifts])

	const shiftMetaMap = useMemo(() => {
		const shiftMap = new Map<
			ShiftOptions,
			{
				title: string
				timeRange: string
			}
		>()
		shiftOptions.forEach(item => {
			shiftMap.set(item.value, {
				title: item.title,
				timeRange: item.description,
			})
		})
		return shiftMap
	}, [shiftOptions])

	const parlorColorMap = useMemo(() => {
		const colors = [
			'#49A6FF',
			'#F5A33A',
			'#FF6EA8',
			'#6FD88A',
			'#B9A0FF',
			'#222222',
		]
		const map = new Map<number, string>()
		;(parlorNames?.items ?? []).forEach((parlor, index) => {
			map.set(parlor.id, colors[index % colors.length])
		})
		return map
	}, [parlorNames])

	const assignments = useMemo<ShiftCellAssignment[]>(() => {
		return filteredSchedules.map(item => {
			const shiftMeta = shiftMetaMap.get(item.period)
			return {
				id: item.id,
				milkerId: item.milker,
				date: moment(item.work_date).startOf('day').valueOf(),
				title: shiftMeta?.title ?? item.period,
				timeRange: shiftMeta?.timeRange ?? '',
				parlorName: item.parlor_name,
				color: parlorColorMap.get(item.parlor) ?? '#49A6FF',
				zones: item.zones,
			}
		})
	}, [filteredSchedules, shiftMetaMap, parlorColorMap])

	const employeeRowHeights = useMemo(() => {
		const dates: number[] = []
		const current = moment(dateRange.startDate).startOf('day')
		const end = moment(dateRange.endDate).startOf('day')

		while (current.isSameOrBefore(end)) {
			dates.push(current.valueOf())
			current.add(1, 'day')
		}

		const assignmentCountByCellKey = new Map<string, number>()
		assignments.forEach(item => {
			const key = `${item.milkerId}-${moment(item.date).startOf('day').valueOf()}`
			assignmentCountByCellKey.set(
				key,
				(assignmentCountByCellKey.get(key) ?? 0) + 1,
			)
		})

		return new Map(
			employees.map(employee => [
				Number(employee.id),
				getEmployeeRowHeight(employee.id, dates, assignmentCountByCellKey),
			]),
		)
	}, [assignments, dateRange.endDate, dateRange.startDate, employees])

	const selectedEmployee = useMemo(
		() =>
			selectedEmployeeId === null
				? null
				: (employees.find(item => Number(item.id) === selectedEmployeeId) ??
					null),
		[employees, selectedEmployeeId],
	)

	const selectedEmployeeName = selectedEmployee
		? `${selectedEmployee.lastname} ${selectedEmployee.firstname}`.trim()
		: ''

	const selectedEmployeeAssignments = useMemo(() => {
		if (selectedEmployeeId === null) return []
		return sortCellAssignments(
			assignments.filter(item => item.milkerId === selectedEmployeeId),
		)
	}, [assignments, selectedEmployeeId])

	const scheduleDatesCount = useMemo(
		() => getDatesInRange(dateRange.startDate, dateRange.endDate).length,
		[dateRange.endDate, dateRange.startDate],
	)

	const {
		scrollContainerRef,
		stretchColumns: stretchScheduleColumns,
		columnWidth: scheduleColumnWidth,
		gridWidth: scheduleGridWidth,
	} = useShiftScheduleScrollLayout(scheduleDatesCount)
	const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0)

	const handleHorizontalScroll = useCallback(() => {
		const scrollLeft = scrollContainerRef.current?.scrollLeft ?? 0
		setHorizontalScrollLeft(scrollLeft)
	}, [scrollContainerRef])

	const handleGridWheel = useCallback(
		(event: React.WheelEvent<HTMLDivElement>) => {
			if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
				event.preventDefault()
			}
		},
		[],
	)

	useEffect(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollLeft = 0
		}
		setHorizontalScrollLeft(0)
	}, [dateRange.endDate, dateRange.startDate, scrollContainerRef])

	const handleFiltersChange = useCallback(
		(filters: {
			dateRange: { startDate: number; endDate: number }
			parlorId: number[] | 'all'
			shift: ShiftOptions[] | 'all'
		}) => {
			setDateRange(filters.dateRange)
			setSelectedParlors(filters.parlorId)
			setSelectedShifts(filters.shift)
		},
		[],
	)

	const getDailyAssignments = useCallback(
		(employeeId: number, date: number) =>
			sortCellAssignments(
				schedules
					.filter(
						item =>
							item.milker === employeeId &&
							moment(item.work_date).startOf('day').valueOf() === date,
					)
					.map(item => {
						const shiftMeta = shiftMetaMap.get(item.period)
						return {
							id: item.id,
							milkerId: item.milker,
							date,
							title: shiftMeta?.title ?? item.period,
							timeRange: shiftMeta?.timeRange ?? '',
							parlorName: item.parlor_name,
							color: parlorColorMap.get(item.parlor) ?? '#49A6FF',
							zones: item.zones,
						}
					}),
			),
		[schedules, shiftMetaMap, parlorColorMap],
	)

	const openAssignDrawer = useCallback(
		(employeeId: number, date: number) => {
			const prefilledParlorId =
				selectedParlors !== 'all' && selectedParlors.length === 1
					? selectedParlors[0]
					: null
			const prefilledShift =
				selectedShifts !== 'all' && selectedShifts.length === 1
					? selectedShifts[0]
					: null

			window.dispatchEvent(
				new CustomEvent('open-assign-shift-drawer', {
					detail: {
						milkerId: employeeId,
						workDate: moment(date).startOf('day').valueOf(),
						parlorId: prefilledParlorId,
						period: prefilledShift,
					},
				}),
			)
		},
		[selectedParlors, selectedShifts],
	)

	const handleAssignmentClick = useCallback(
		(payload: {
			employeeId: number
			date: number
			assignments: ShiftCellAssignment[]
		}) => {
			const cellKey = `${payload.employeeId}-${payload.date}`
			const employee = employees.find(
				item => Number(item.id) === payload.employeeId,
			)
			const employeeName = employee
				? `${employee.lastname} ${employee.firstname}`.trim()
				: 'Сотрудник'
			const dailyAssignments = getDailyAssignments(
				payload.employeeId,
				payload.date,
			)

			setSelectedCellKey(cellKey)
			setSelectedEmployeeId(null)
			setDetailsDrawerData({
				employeeName,
				assignments: dailyAssignments,
			})
		},
		[employees, getDailyAssignments],
	)

	const handleAddShiftClick = useCallback(
		(payload: { employeeId: number; date: number }) => {
			if (isPastScheduleDate(payload.date)) return

			setSelectedCellKey(null)
			setDetailsDrawerData(null)
			setSelectedEmployeeId(null)
			openAssignDrawer(payload.employeeId, payload.date)
		},
		[openAssignDrawer],
	)
	const { mutate: deleteShift, isPending: isDeletingShift } = useMutation({
		mutationFn: ({
			id,
		}: {
			id: number
			employeeName: string
			timeRange: string
			parlorName: string
		}) => MilkingProcessService.deleteMilkingSchedule(id),
		onSuccess: async (
			_,
			{ id: deletedId, employeeName, timeRange, parlorName },
		) => {
			await queryClient.invalidateQueries({
				queryKey: ['milking-schedules-grid'],
			})

			setDetailsDrawerData(prev => {
				if (!prev) return prev
				const nextAssignments = prev.assignments.filter(
					item => item.id !== deletedId,
				)
				if (nextAssignments.length === 0) {
					setSelectedCellKey(null)
					return null
				}
				return {
					...prev,
					assignments: nextAssignments,
				}
			})
			setShiftToDelete(null)
			dispatch(
				notifyUser({
					title: 'Смена удалена',
					description: `Смена ${timeRange} (${parlorName}) удалена из расписания ${employeeName}.`,
					isError: false,
				}),
			)
		},
		onError: () => {
			dispatch(
				notifyUser({
					title: 'Произошла ошибка',
					description: 'Попробуйте снова.',
					isError: true,
				}),
			)
		},
	})

	const handleEmployeeClick = useCallback((employee: ShiftEmployee) => {
		const employeeId = Number(employee.id)
		setSelectedEmployeeId(prev => (prev === employeeId ? null : employeeId))
		setDetailsDrawerData(null)
		setSelectedCellKey(null)
	}, [])

	const handleDeleteShiftClick = (
		item: ShiftCellAssignment,
		employeeName: string,
	) => {
		if (isPastScheduleDate(item.date)) return
		setShiftToDelete({
			id: item.id,
			employeeName,
			title: item.title,
			timeRange: item.timeRange,
			parlorName: item.parlorName,
		})
	}

	const handleConfirmDeleteShift = () => {
		if (!shiftToDelete) return
		deleteShift({
			id: shiftToDelete.id,
			employeeName: shiftToDelete.employeeName,
			timeRange: shiftToDelete.timeRange,
			parlorName: shiftToDelete.parlorName,
		})
	}

	return (
		<Stack
			direction='column'
			gap='24px'
			sx={{ p: '0 16px', minWidth: 0, width: '100%' }}
		>
			<MilkingShiftsHeader onFiltersChange={handleFiltersChange} />

			<Box
				sx={{
					maxHeight: SHIFT_SCHEDULE_VERTICAL_SCROLL_MAX_HEIGHT,
					overflowY: 'auto',
					overflowX: 'visible',
					width: '100%',
					pb: '16px',
					...hideScrollbarSx,
				}}
			>
				<Box
					sx={{
						position: 'sticky',
						top: 0,
						zIndex: SHIFT_SCHEDULE_STICKY_TOP_Z_INDEX,
						backgroundColor: COLORS.background,
						pb: '12px',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
						<Box
							sx={{
								width: `${SHIFT_EMPLOYEE_COLUMN_WIDTH}px`,
								flexShrink: 0,
							}}
						>
							<Stack
								direction='row'
								alignItems='center'
								gap='4px'
								sx={{ minHeight: '24px' }}
							>
								<UserIcon />
								<TypographyPrimary>{employees.length}</TypographyPrimary>
							</Stack>
						</Box>
						<Box
							sx={{
								flex: '1 1 0',
								minWidth: 0,
								width: 0,
								overflow: 'hidden',
								pl: '12px',
							}}
						>
							<Box
								sx={{
									width: stretchScheduleColumns
										? '100%'
										: `${scheduleGridWidth}px`,
									minWidth: stretchScheduleColumns
										? '100%'
										: `${scheduleGridWidth}px`,
									transform: `translateX(-${horizontalScrollLeft}px)`,
									willChange: 'transform',
								}}
							>
								<ShiftScheduleHeader
									dateRange={dateRange}
									count={employees.length}
									datesOnly
									columnWidth={scheduleColumnWidth}
								/>
							</Box>
						</Box>
					</Box>
					<Divider sx={{ mt: '12px' }} />
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						width: '100%',
						minWidth: 0,
						overflow: 'visible',
					}}
				>
					<Box
						sx={{
							width: `${SHIFT_EMPLOYEE_COLUMN_WIDTH}px`,
							flexShrink: 0,
							overflow: 'visible',
						}}
					>
						<ShiftEmployeeList
							employees={employees}
							employeeRowHeights={employeeRowHeights}
							selectedEmployeeId={selectedEmployeeId}
							onEmployeeClick={handleEmployeeClick}
						/>
					</Box>

					<Box
						sx={{
							flex: '1 1 0',
							minWidth: 0,
							width: 0,
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Box
							ref={scrollContainerRef}
							onScroll={handleHorizontalScroll}
							onWheel={handleGridWheel}
							sx={{
								flex: '1 1 auto',
								minHeight: 0,
								overflowX: 'auto',
								overflowY: 'visible',
								pl: '12px',
								pt: '2px',
								...hideScrollbarSx,
							}}
						>
							<Box
								sx={{
									flexShrink: 0,
									width: stretchScheduleColumns
										? '100%'
										: `${scheduleGridWidth}px`,
									minWidth: stretchScheduleColumns
										? '100%'
										: `${scheduleGridWidth}px`,
								}}
							>
								<ShiftScheduleGrid
									dateRange={dateRange}
									columnWidth={scheduleColumnWidth}
									employees={employees}
									assignments={assignments}
									selectedCellKey={selectedCellKey}
									employeeRowHeights={employeeRowHeights}
									onAssignmentClick={handleAssignmentClick}
									onAddClick={handleAddShiftClick}
								/>
							</Box>
						</Box>
						<ShiftScheduleHorizontalScrollbar
							scrollRef={scrollContainerRef}
							visible={!stretchScheduleColumns}
						/>
					</Box>
				</Box>
			</Box>
			<ShiftDayDetailsDrawer
				open={Boolean(detailsDrawerData)}
				employeeName={detailsDrawerData?.employeeName ?? ''}
				assignments={detailsDrawerData?.assignments ?? []}
				onClose={() => {
					setDetailsDrawerData(null)
					setSelectedCellKey(null)
				}}
				onDeleteShift={item =>
					handleDeleteShiftClick(item, detailsDrawerData?.employeeName ?? '')
				}
			/>
			<ShiftEmployeeDrawer
				open={selectedEmployeeId !== null}
				employeeName={selectedEmployeeName}
				dateRange={dateRange}
				assignments={selectedEmployeeAssignments}
				onClose={() => setSelectedEmployeeId(null)}
				onDeleteShift={item =>
					handleDeleteShiftClick(item, selectedEmployeeName)
				}
			/>
			<DeleteShiftDialog
				open={Boolean(shiftToDelete)}
				title='Удалить смену?'
				description={
					shiftToDelete
						? `Смена ${shiftToDelete.timeRange} (${shiftToDelete.parlorName}) будет удалена из расписания ${shiftToDelete.employeeName}.`
						: ''
				}
				onClose={() => setShiftToDelete(null)}
				onConfirm={handleConfirmDeleteShift}
				isLoading={isDeletingShift}
			/>
		</Stack>
	)
}
